/**
 * Tema yalnızca kök öğedeki `data-theme` niteliğini değiştirir; renkler
 * `styles/tokens.css` içindeki jetonlardan gelir. Seçim `localStorage`'da
 * tutulur — kullanıcı tercihi, portföy verisi değil.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type ThemeChoice = "system" | "light" | "dark";

const STORAGE_KEY = "parafon.theme";

interface ThemeContextValue {
  readonly choice: ThemeChoice;
  readonly setChoice: (choice: ThemeChoice) => void;
  /** Fiilen uygulanan tema (sistem çözümlenmiş hâli). */
  readonly resolved: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): ThemeChoice {
  try {
    const v = globalThis.localStorage?.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch {
    return "system";
  }
}

function systemPrefersDark(): boolean {
  try {
    return globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches === true;
  } catch {
    return false;
  }
}

export function ThemeProvider({ children }: { readonly children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStored);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  useEffect(() => {
    const mq = globalThis.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = (e: MediaQueryListEvent): void => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const root = globalThis.document?.documentElement;
    if (!root) return;
    if (choice === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", choice);
  }, [choice]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, next);
    } catch {
      // Depolama kapalıysa tercih oturumla sınırlı kalır — kabul edilebilir.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      choice,
      setChoice,
      resolved: choice === "dark" || (choice === "system" && systemDark) ? "dark" : "light",
    }),
    [choice, setChoice, systemDark],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) throw new Error("useTheme yalnızca ThemeProvider içinde kullanılabilir.");
  return ctx;
}
